/**
 * The amount: typed straight in, or a digit at a time.
 *
 * The value is integer cents and never a string — `src/lib/pad.ts` says why —
 * so there is nothing here that rounds or reformats. What the pad does hold,
 * briefly, is `typed`: the text between somebody's first keystroke in the box
 * and the moment they leave it. It is local to this component on purpose. The
 * sheet around it keeps an amount, not a half-typed string, and a `12.` on its
 * way to `12.50` is nobody else's business.
 *
 * There are two ways in and they are both real. The box is where the number
 * is, so tapping the number is what opens the phone's own keypad — `12.50`
 * typed the way it is written, read by `parseMoneyInput` like every other
 * money box in this app. The keys underneath are digits from the right, for
 * one-handed entry with no decimal point to hunt for. Whichever is touched,
 * the other shows the same amount an instant later, because there is only one
 * amount: `amountCents`, held by the sheet.
 *
 * The keys stay because a text box is not a substitute for them on a phone,
 * and the whole group still takes the keyboard, because a control that only
 * works with a pointer is a control half the people using it cannot reach.
 * The reading is announced politely rather than assertively, so a screen
 * reader says the running total between key presses instead of interrupting
 * each one — and it goes quiet while somebody is typing, since the box is
 * already reading their own keystrokes back to them.
 */

import { useState } from 'react'
import { formatMoney, moneySymbol } from '../lib/format'
import { padBackspace, padDigit, padInput, padKey, padTyped } from '../lib/pad'

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'] as const

export function AmountPad({
  amountCents,
  currency,
  onChange,
  labelledBy,
}: {
  amountCents: number
  currency: string
  onChange: (cents: number) => void
  labelledBy: string
}) {
  const [typed, setTyped] = useState<string | null>(null)
  const text = typed ?? padInput(amountCents)

  /**
   * A press on a key. It settles the box first: the amount it produces is
   * `amountCents` shifted up, so leaving `12.` sitting there next to `$120.05`
   * would be the pad showing two different numbers.
   */
  const press = (cents: number) => {
    setTyped(null)
    onChange(cents)
  }

  return (
    <div
      className="pad"
      role="group"
      aria-labelledby={labelledBy}
      tabIndex={0}
      onKeyDown={(event) => {
        // The box takes its own keys. A digit typed in there is text on its way
        // through `parseMoneyInput`, and a Backspace deletes a character rather
        // than a digit off the end of the number.
        if (event.target instanceof HTMLInputElement) return
        if (event.metaKey || event.ctrlKey || event.altKey) return
        const next = padKey(amountCents, event.key)
        if (next === null) return
        event.preventDefault()
        press(next)
      }}
    >
      <div className="pad__amount">
        {/* The digits are in the box; the symbol is not, so that what the box
            holds is exactly what `parseMoneyInput` reads. The screen reader
            gets both together, from the reading below. */}
        <span className="pad__symbol" aria-hidden="true">
          {moneySymbol(currency).trim()}
        </span>
        <input
          className="pad__input"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          aria-labelledby={labelledBy}
          value={text}
          // Tapping the amount means "I am about to say what this is", not "I
          // want the caret after the 0.00" — so the old value goes when the
          // first character lands, and Backspace still works if it doesn't.
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event) => {
            const box = event.currentTarget
            const entry = { cents: amountCents, typed }
            const next = padTyped(entry, box.value)

            if (next === entry) {
              // Refused. Nothing in React changed, so nothing re-renders and
              // the character would be left sitting in the DOM: put the box
              // back by hand, with the caret where it was before the key.
              const at = Math.max(0, (box.selectionStart ?? 0) - (box.value.length - text.length))
              box.value = text
              box.setSelectionRange(at, at)
              return
            }

            setTyped(next.typed)
            if (next.cents !== amountCents) onChange(next.cents)
          }}
          // Leaving tidies up: `12.` is $12.00 and always was, and the box now
          // says so. Nothing about the amount changes here.
          onBlur={() => setTyped(null)}
        />
      </div>

      {/*
        While somebody is typing, this says nothing: the box is announcing
        their own keystrokes, and a live region repeating each one on top of
        that is two voices for one number.
      */}
      <output className="visually-hidden" aria-live="polite">
        {typed === null ? formatMoney(amountCents, currency) : ''}
      </output>

      <div className="pad__keys">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className="pad__key"
            onClick={() => press(padDigit(amountCents, Number(key)))}
          >
            {key}
          </button>
        ))}
        <button
          type="button"
          className="pad__key pad__key--wide"
          onClick={() => press(padBackspace(amountCents))}
        >
          <span aria-hidden="true">⌫</span>
          <span className="visually-hidden">Delete the last digit</span>
        </button>
      </div>
    </div>
  )
}
